/**
 * The Spells panel.
 *
 * **Nothing here computes a slot.** Capacity, free slots and the list of what
 * may legally be memorised all arrive from `GET /api/campaigns/:id/spells` —
 * a priest's slots are a cumulative read across two tables with a Wisdom gate
 * on top, and sphere access is prose parsed server-side. A client-side copy of
 * any of it would still render a confident number after the data moved.
 *
 * Two things the UI has to say out loud, because both are the opposite of what
 * a modern player expects:
 *
 *  - **Resting does not reset the loadout.** Memorising is a top-up of the
 *    slots that casting emptied, and an uncast spell cannot be swapped out.
 *  - **A spent slot stays visible**, greyed, so it is obvious what there is to
 *    re-study — and so a player is never told a spell is "gone" without being
 *    shown which one.
 */

import { useState } from 'react';

import { optionLabel } from '../characters/options.js';
import { describeCopyResult, type CasterKind, type CopyResult, type OfferedSpell, type SpellSheet } from './sheet.js';

export interface SpellsPanelProps {
  /** Fetched once per table by `useSpellSheet` and shared with the combat window. */
  readonly sheet: SpellSheet;
  readonly onMemorise: (
    entries: readonly {
      readonly name: string;
      readonly casterKind: CasterKind;
      /** Conjure Elemental's own printed choice — absent for every other spell. */
      readonly elementType?: string;
    }[],
    /** A retainer of the viewer's. Absent means the viewer themselves. */
    characterId?: string,
  ) => void;
  readonly onCast: (name: string, characterId?: string) => void;
  /**
   * Takes the free spell a level brought. Absent renders no picker at all —
   * a panel that offered the choice and could not send it would be worse than
   * one that never mentioned it.
   */
  readonly onLearn?: (name: string, characterId?: string) => void;
  /**
   * Copies a spell from a scroll in the pack into the book, for gold and a day.
   * Resolves with the server's report, rejects with its reason; absent renders no
   * copying section.
   */
  readonly onCopy?: (name: string, characterId?: string, fromCharacterId?: string) => Promise<CopyResult>;
  /** Lends one of this wizard's spells to another, for one copy; withdraws it; buys another blank book. */
  readonly onLend?: (spell: string, toCharacterId: string, characterId?: string) => Promise<void>;
  readonly onUnlend?: (spell: string, toCharacterId: string, characterId?: string) => Promise<void>;
  readonly onBuyBook?: (characterId?: string) => Promise<string>;
  /** In a fight, casting is a declaration in the combat window, not here. */
  readonly inCombat?: boolean;
  /**
   * Whose loadout is being shown, and the casters this player may switch to.
   *
   * **A retainer's spells are unreachable without this.** A henchman has no
   * login, and memorisation is the only way a spent slot ever comes back — so a
   * hired caster casts what they arrived holding and is a liability for the rest
   * of the campaign. The server has accepted a `characterId` on `spell:memorise`
   * since the sheets were opened up; this is the surface that sends one.
   */
  readonly casters?: readonly { readonly characterId: string; readonly name: string }[];
  readonly showing?: string;
  readonly onShow?: (characterId: string) => void;
}

export function SpellsPanel({
  sheet,
  onMemorise,
  onCast,
  inCombat,
  casters = [],
  onLearn,
  onCopy,
  onLend,
  onUnlend,
  onBuyBook,
  showing = '',
  onShow,
}: SpellsPanelProps) {
  const [picked, setPicked] = useState<string[]>([]);
  // Conjure Elemental's own printed choice, per checked row — a `Record`
  // rather than a single value, since more than one row could in principle
  // be checked at once. Keyed identically to `picked`.
  const [elementTypes, setElementTypes] = useState<Record<string, string>>({});
  const [learning, setLearning] = useState('');
  const [copyNotice, setCopyNotice] = useState<string | null>(null);
  const [lendSpell, setLendSpell] = useState('');
  const [lendTo, setLendTo] = useState('');

  // The switcher has to render even when *this* sheet casts nothing: a fighter
  // with a hired wizard would otherwise have no way to reach the wizard's book.
  //
  // **Said, not blank.** A character with no casting class and nobody to switch to used to
  // get nothing under the tab at all: the page looked broken. It is not an error, so it
  // gets a heading and one plain sentence about why and what would change it.
  if (!sheet.casts && casters.length === 0) {
    return (
      <section className="space-y-2 rounded-md border border-neutral-300 p-3 dark:border-neutral-700">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Spells</h2>
        <p className="text-sm">This character casts no spells.</p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          Spells come with a casting class — a mage, a priest, a druid or a bard — and a fighter or
          a thief has none. If you hire a spellcaster as a henchman, their spells appear here for you
          to prepare and cast.
        </p>
      </section>
    );
  }

  const held = sheet.memorised.filter((entry) => !entry.expended);
  const spent = sheet.memorised.filter((entry) => entry.expended);

  const pickedSpells = picked
    .map((key) => sheet.available.find((s) => keyOf(s) === key))
    .filter((s): s is OfferedSpell => s !== undefined);
  const missingElementType = !readyToMemorise(pickedSpells, elementTypes);

  const submit = () => {
    const entries = entriesToMemorise(pickedSpells, elementTypes);
    if (entries.length > 0) onMemorise(entries, showing || undefined);
    setPicked([]);
    setElementTypes({});
  };

  return (
    <section className="space-y-2 rounded-md border border-neutral-300 p-3 dark:border-neutral-700">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Spells</h2>

      {/* Only when there is somebody to switch to. A caster with no retainers
          sees exactly what they saw before. */}
      {casters.length > 0 && (
        <select
          aria-label="Whose spells"
          value={showing}
          onChange={(e) => onShow?.(e.target.value)}
          className="w-full rounded-md border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
        >
          <option value="">your spells</option>
          {casters.map((caster) => (
            <option key={caster.characterId} value={caster.characterId}>
              {caster.name}
            </option>
          ))}
        </select>
      )}

      {!sheet.casts && <p className="text-xs text-neutral-500 dark:text-neutral-400">They cast no spells.</p>}

      {/* **The free spell a level brings**, which nothing granted and nothing
          could spend until now: `spellbook` is written at creation and refused
          on a sheet PATCH, so a mage reaching 5th level gained slots it could
          never fill. The book makes the spell automatic and says nothing about
          who picks it; the player does. The offer is the server's — "of a
          level they can now cast", with Intelligence capping both how high and
          how many per level. */}
      {onLearn && (sheet.toLearn ?? 0) > 0 && (
        <div className="space-y-1 rounded-md border border-emerald-700/50 p-2">
          <p className="text-xs font-medium text-emerald-800 dark:text-emerald-300">
            {sheet.toLearn} new spell{(sheet.toLearn ?? 0) > 1 ? 's' : ''} to learn
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              aria-label="A spell to learn"
              value={learning}
              onChange={(e) => setLearning(e.target.value)}
              className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
            >
              <option value="">choose a spell…</option>
              {(sheet.learnable ?? []).map((entry) => (
                <option key={entry.name} value={entry.name}>
                  {entry.name} (level {entry.level})
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={learning === ''}
              onClick={() => {
                onLearn(learning, showing || undefined);
                setLearning('');
              }}
              className="rounded-md border border-neutral-300 px-2 py-1 text-xs disabled:opacity-40 dark:border-neutral-700"
            >
              Learn it
            </button>
          </div>
        </div>
      )}

      {/* **Copying from a scroll**, the paid way to learn a spell the level did not
          grant. The book, its pages and every price are the server's: what a
          page costs depends on the kind of book, and the pages a spell takes are
          rolled once and kept. */}
      {onCopy && sheet.copying && (
        <div className="space-y-1 rounded-md border border-neutral-300 p-2 text-xs dark:border-neutral-700">
          <p className="font-medium">
            Spellbook: {optionLabel(sheet.copying.bookType)} · {sheet.copying.pagesUsed}
            {sheet.copying.unmeasured > 0 ? '+' : ''} of {sheet.copying.capacity} pages used{sheet.copying.books > 1 ? ` in ${sheet.copying.books} books` : ''}
            {sheet.copying.unmeasured > 0
              ? ` (${sheet.copying.unmeasured} older spell${sheet.copying.unmeasured > 1 ? 's' : ''} not yet measured)`
              : ''}{' '}
            · {sheet.copying.costPerPage} a page to copy into
          </p>
          {sheet.copying.scrolls.length === 0 && (
            <p className="text-neutral-500 dark:text-neutral-400">
              No wizard scroll in the pack to copy from.
            </p>
          )}
          <ul className="space-y-0.5">
            {sheet.copying.scrolls.map((scroll) => (
              <li key={scroll.item} className="flex flex-wrap items-baseline gap-2">
                <span className="flex-1">
                  {scroll.spell} <span className="text-neutral-500 dark:text-neutral-400">L{scroll.level}</span>
                  <span className="text-neutral-500 dark:text-neutral-400">
                    {' '}
                    ·{' '}
                    {scroll.cost
                      ? `${scroll.pages} pages, ${scroll.cost}`
                      : `up to ${scroll.maxCost}, pages rolled when you start`}
                  </span>
                  {scroll.barred && (
                    <span className="text-amber-700 dark:text-amber-400"> — {scroll.barred}</span>
                  )}
                </span>
                <button
                  type="button"
                  aria-label={`Copy ${scroll.spell}`}
                  disabled={inCombat === true || scroll.barred !== undefined}
                  onClick={() => {
                    setCopyNotice(null);
                    onCopy(scroll.spell, showing || undefined)
                      .then((result) => setCopyNotice(describeCopyResult(result)))
                      .catch((e: unknown) =>
                        setCopyNotice(e instanceof Error && e.message ? e.message : 'could not copy that'),
                      );
                  }}
                  className="rounded-md border border-neutral-300 px-2 py-0.5 disabled:opacity-40 dark:border-neutral-700"
                >
                  Copy (a day)
                </button>
              </li>
            ))}
          </ul>
          {(sheet.copying.sources?.length ?? 0) > 0 && (
            <ul className="space-y-0.5">
              {sheet.copying.sources!.map((source) => (
                <li key={`${source.characterId}-${source.spell}`} className="flex flex-wrap items-baseline gap-2">
                  <span className="flex-1">
                    {source.spell} <span className="text-neutral-500 dark:text-neutral-400">L{source.level} · from {source.name}'s spellbook{source.via === 'lent' ? ' (lent to you)' : ''}</span>
                  </span>
                  <button
                    type="button"
                    aria-label={`Copy ${source.spell} from ${source.name}`}
                    disabled={inCombat === true}
                    onClick={() => {
                      setCopyNotice(null);
                      onCopy(source.spell, showing || undefined, source.characterId)
                        .then((result) => setCopyNotice(describeCopyResult(result)))
                        .catch((e: unknown) =>
                          setCopyNotice(e instanceof Error && e.message ? e.message : 'could not copy that'),
                        );
                    }}
                    className="rounded-md border border-neutral-300 px-2 py-0.5 disabled:opacity-40 dark:border-neutral-700"
                  >
                    Copy (a day)
                  </button>
                </li>
              ))}
            </ul>
          )}
          {onLend && (sheet.copying.wizards?.length ?? 0) > 0 && sheet.spellbook.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <select
                aria-label="A spell to lend"
                value={lendSpell}
                onChange={(e) => setLendSpell(e.target.value)}
                className="rounded-md border border-neutral-300 bg-white px-2 py-1 dark:border-neutral-700 dark:bg-neutral-900"
              >
                <option value="">lend a spell…</option>
                {sheet.spellbook.map((s) => (
                  <option key={s.name} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
              <select
                aria-label="Who to lend it to"
                value={lendTo}
                onChange={(e) => setLendTo(e.target.value)}
                className="rounded-md border border-neutral-300 bg-white px-2 py-1 dark:border-neutral-700 dark:bg-neutral-900"
              >
                <option value="">to…</option>
                {sheet.copying.wizards!.map((w) => (
                  <option key={w.characterId} value={w.characterId}>
                    {w.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={lendSpell === '' || lendTo === ''}
                onClick={() => {
                  setCopyNotice(null);
                  onLend(lendSpell, lendTo, showing || undefined)
                    .then(() => setCopyNotice(`Lent ${lendSpell} for one copy.`))
                    .catch((e: unknown) =>
                      setCopyNotice(e instanceof Error && e.message ? e.message : 'could not lend that'),
                    );
                  setLendSpell('');
                  setLendTo('');
                }}
                className="rounded-md border border-neutral-300 px-2 py-0.5 disabled:opacity-40 dark:border-neutral-700"
              >
                Lend (one copy)
              </button>
            </div>
          )}
          {onUnlend && (sheet.copying.lent?.length ?? 0) > 0 && (
            <ul className="space-y-0.5">
              {sheet.copying.lent!.map((loan) => (
                <li key={`${loan.spell}-${loan.toCharacterId}`} className="flex items-baseline gap-2">
                  <span className="flex-1">
                    {loan.spell} lent to {loan.toName}
                  </span>
                  <button
                    type="button"
                    aria-label={`Take back ${loan.spell} from ${loan.toName}`}
                    onClick={() =>
                      void onUnlend(loan.spell, loan.toCharacterId, showing || undefined).catch(() => undefined)
                    }
                    className="text-neutral-500 underline dark:text-neutral-400"
                  >
                    take back
                  </button>
                </li>
              ))}
            </ul>
          )}
          {onBuyBook && (
            <button
              type="button"
              onClick={() => {
                setCopyNotice(null);
                onBuyBook(showing || undefined)
                  .then((message) => setCopyNotice(message))
                  .catch((e: unknown) =>
                    setCopyNotice(e instanceof Error && e.message ? e.message : 'could not buy a book'),
                  );
              }}
              className="rounded-md border border-neutral-300 px-2 py-0.5 dark:border-neutral-700"
            >
              Buy another blank book ({sheet.copying.blankBookCost})
            </button>
          )}
          {copyNotice && (
            <p role="status" id="copy-notice" className="text-amber-800 dark:text-amber-300">
              {copyNotice}
            </p>
          )}
        </div>
      )}

      {sheet.profiles.map((profile) => (
        <p key={profile.characterClass} className="text-xs text-neutral-500 dark:text-neutral-400">
          {profile.characterClass} {profile.classLevel} · casts at level {profile.casterLevel} ·
          slots {profile.slots.join('/') || 'none'}
        </p>
      ))}

      <ul className="space-y-0.5 text-sm">
        {held.map((entry, index) => (
          <li key={`${entry.name}-${index}`} className="flex items-baseline gap-2">
            <span className="flex-1">
              {entry.name} <span className="text-xs text-neutral-500 dark:text-neutral-400">L{entry.level}</span>
              {entry.elementType && (
                <span className="text-xs text-neutral-500 dark:text-neutral-400"> · {entry.elementType}</span>
              )}
            </span>
            {!inCombat && (
              <button
                type="button"
                onClick={() => onCast(entry.name)}
                className="rounded border border-neutral-300 px-2 text-xs dark:border-neutral-700"
              >
                Cast
              </button>
            )}
          </li>
        ))}
        {/* Spent slots stay on the list. They are what a rest tops up, and the
            only record of what was cast. */}
        {spent.map((entry, index) => (
          <li key={`spent-${entry.name}-${index}`} className="text-sm text-neutral-500 dark:text-neutral-400 line-through">
            {entry.name} <span className="text-xs">L{entry.level}</span>
          </li>
        ))}
        {sheet.memorised.length === 0 && (
          <li className="text-xs text-neutral-500 dark:text-neutral-400">nothing memorised</li>
        )}
      </ul>

      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        free:{' '}
        {(['wizard', 'priest'] as const)
          .filter((kind) => (sheet.free[kind]?.length ?? 0) > 0)
          .map((kind) => `${kind} ${sheet.free[kind]!.join('/')}`)
          .join(' · ') || 'none'}
      </p>

      <details>
        <summary className="cursor-pointer text-xs text-neutral-500 dark:text-neutral-400">
          Study / pray ({sheet.available.length} available)
        </summary>
        {/* Stated, not implied: this is the rule players get wrong first. */}
        <p className="py-1 text-xs text-neutral-500 dark:text-neutral-400">
          A memorised spell stays until it is cast. This fills empty slots only — 10 minutes of
          game time per spell level, per spell.
        </p>
        <ul className="max-h-56 space-y-0.5 overflow-y-auto text-sm">
          {sheet.available.map((entry) => (
            <li key={keyOf(entry)}>
              <label className="flex items-baseline gap-2">
                <input
                  type="checkbox"
                  checked={picked.includes(keyOf(entry))}
                  onChange={(e) =>
                    setPicked((current) =>
                      e.target.checked
                        ? [...current, keyOf(entry)]
                        : current.filter((k) => k !== keyOf(entry)),
                    )
                  }
                />
                <span>
                  {entry.name}{' '}
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">
                    L{entry.level} · {entry.sphere ?? entry.school} · casting time{' '}
                    {entry.castingTime}
                  </span>
                  {entry.resolved && (
                    <span
                      className="ml-1 rounded border border-emerald-600/40 px-1 text-[10px] text-emerald-700 dark:text-emerald-400"
                      title="The engine works out this spell's effect. Otherwise the DM narrates it."
                    >
                      engine
                    </span>
                  )}
                </span>
              </label>
              {/* **Only the rare spell that offers one** (Conjure Elemental's
                  own "type of elemental must be chosen when the spell is
                  memorized", and Fire Shield's warm or chill), and only once
                  its own checkbox is checked —
                  offering a choice for a spell nobody is memorising invites
                  a decision that goes nowhere. */}
              {entry.elementTypes && picked.includes(keyOf(entry)) && (
                <select
                  aria-label={`Choice for ${entry.name}`}
                  value={elementTypes[keyOf(entry)] ?? ''}
                  onChange={(e) =>
                    setElementTypes((current) => ({ ...current, [keyOf(entry)]: e.target.value }))
                  }
                  className="ml-6 mt-0.5 rounded-md border border-neutral-300 bg-white px-2 py-0.5 text-xs dark:border-neutral-700 dark:bg-neutral-900"
                >
                  <option value="">choose one…</option>
                  {entry.elementTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              )}
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={submit}
          disabled={picked.length === 0 || missingElementType}
          className="mt-1 rounded-md border border-neutral-300 px-3 py-1 text-sm disabled:opacity-50 dark:border-neutral-700"
        >
          Memorise {picked.length > 0 ? `(${picked.length})` : ''}
        </button>
      </details>
    </section>
  );
}

/** A spell is unique by name *within a list*, not across both. */
export function keyOf(entry: OfferedSpell): string {
  return `${entry.casterKind}:${entry.name}`;
}

/**
 * Whether every checked spell that offers an element actually has one
 * chosen — pure and exported for the same reason `spellModesFor`
 * (`apps/client/src/combat/CombatPanel.tsx`) is: which rows are checked and
 * which elements are picked are this form's own local state, invisible to
 * `renderToStaticMarkup`, so the only place this logic can be asserted is
 * here directly rather than by rendering and hoping the button's `disabled`
 * attribute shows it.
 */
export function readyToMemorise(
  pickedSpells: readonly OfferedSpell[],
  elementTypes: Readonly<Record<string, string>>,
): boolean {
  if (pickedSpells.length === 0) return false;
  return !pickedSpells.some((s) => s.elementTypes && !elementTypes[keyOf(s)]);
}

/** The wire payload `onMemorise` actually receives, built from the same two inputs. */
export function entriesToMemorise(
  pickedSpells: readonly OfferedSpell[],
  elementTypes: Readonly<Record<string, string>>,
): readonly { readonly name: string; readonly casterKind: CasterKind; readonly elementType?: string }[] {
  return pickedSpells.map((s) => ({
    name: s.name,
    casterKind: s.casterKind,
    ...(s.elementTypes ? { elementType: elementTypes[keyOf(s)] } : {}),
  }));
}
